import { invalidateInventoryMovement } from '@/utils/inventoryQueries'
import { useInventoryCatalogSearch } from '@/hooks/useInventoryCatalogSearch'
import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeftIcon, TrendingDown, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { movimentiApi, giacenzeProdottiApi, giacenzeComponentiApi, giacenzeBoxApi } from '@/api/magazzino'

/**
 * Recupera tutti i risultati da endpoint DRF paginati (page-based).
 * Se la risposta non e paginata, restituisce direttamente l'array.
 */
const fetchAllPaginatedResults = async (apiGetter, params = {}) => {
  const allResults = []
  let page = 1
  let safetyCounter = 0

  while (safetyCounter < 100) {
    const response = await apiGetter({ ...params, page })
    const payload = response?.data

    if (Array.isArray(payload)) {
      return payload
    }

    if (!payload || !Array.isArray(payload.results)) {
      return []
    }

    allResults.push(...payload.results)

    if (!payload.next) {
      return allResults
    }

    const nextPageRaw = new URL(payload.next, 'http://localhost').searchParams.get('page')
    const nextPage = Number.parseInt(nextPageRaw || '', 10)
    page = Number.isFinite(nextPage) && nextPage > 0 ? nextPage : page + 1
    safetyCounter += 1
  }

  return allResults
}

/**
 * Form per scarico merce da magazzino.
 * Permette di scaricare multipli prodotti con validazione giacenza disponibile.
 */
export default function ScaricoForm() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [tipoItem, setTipoItem] = useState('prodotto')
  const [itemsSelezionati, setItemsSelezionati] = useState([])
  const [searchItem, setSearchItem] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [causale, setCausale] = useState('')
  const [categoriaCausale, setCategoriaCausale] = useState('')
  const [note, setNote] = useState('')
  const dropdownRef = useRef(null)

  const { items: itemsFiltrati, isSearching } = useInventoryCatalogSearch(tipoItem, searchItem)

  // Query giacenze prodotti per validazione
  const { data: giacenzeProdottiData } = useQuery({
    queryKey: ['giacenze-prodotti', 'all-pages'],
    queryFn: () => fetchAllPaginatedResults((params) => giacenzeProdottiApi.getAll(params)),
    enabled: tipoItem === 'prodotto',
  })

  // Query giacenze componenti per validazione
  const { data: giacenzeComponentiData } = useQuery({
    queryKey: ['giacenze-componenti', 'all-pages'],
    queryFn: () => fetchAllPaginatedResults((params) => giacenzeComponentiApi.getAll(params)),
    enabled: tipoItem === 'componente',
  })

  const { data: giacenzeBoxData } = useQuery({
    queryKey: ['giacenze-box', 'all-pages'],
    queryFn: () => fetchAllPaginatedResults((params) => giacenzeBoxApi.getAll(params)),
    enabled: tipoItem === 'box',
  })

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Mutation scarico multiplo
  const scaricoMutation = useMutation({
    mutationFn: async (items) => {
      const promises = items.map(item => movimentiApi.scarico(item))
      return Promise.all(promises)
    },
    onSuccess: async () => {
      await invalidateInventoryMovement(queryClient)
      toast.success('Scarico registrato con successo')
      navigate('/magazzino/movimenti')
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.detail || 'Errore durante lo scarico'
      toast.error(errorMsg)
    },
  })

  const giacenzeProdotti = Array.isArray(giacenzeProdottiData)
    ? giacenzeProdottiData
    : []

  const giacenzeComponenti = Array.isArray(giacenzeComponentiData)
    ? giacenzeComponentiData
    : []

  const giacenzeBox = Array.isArray(giacenzeBoxData) ? giacenzeBoxData : []

  // Trova disponibilità da usare nel form.
  // Per componenti usiamo la quantità effettiva (disponibile - riservata).
  const getDisponibilitaItem = (itemId) => {
    if (tipoItem === 'prodotto') {
      const giacenza = giacenzeProdotti.find(g => g.prodotto === itemId)
      const disponibile = giacenza?.quantita_disponibile || 0
      return {
        disponibile,
        riservata: giacenza?.quantita_riservata || 0,
        effettiva: giacenza?.quantita_effettiva ?? disponibile,
      }
    }

    const giacenza = tipoItem === 'box'
      ? giacenzeBox.find(g => g.tipologia === itemId)
      : giacenzeComponenti.find(g => g.componente === itemId)
    const disponibile = giacenza?.quantita_disponibile || 0
    const riservata = giacenza?.quantita_riservata || 0
    const effettiva = giacenza?.quantita_effettiva ?? (disponibile - riservata)
    return { disponibile, riservata, effettiva }
  }

  const addItem = (item) => {
    if (itemsSelezionati.find(i => i.id === item.id)) {
      toast.error(`${tipoItem === 'prodotto' ? 'Prodotto' : 'Componente'} già aggiunto`)
      return
    }

    const disponibilita = getDisponibilitaItem(item.id)
    const maxScaricabile = tipoItem === 'componente'
      ? disponibilita.effettiva
      : disponibilita.disponibile

    if (maxScaricabile <= 0) {
      toast.error(`Giacenza non disponibile per questo ${tipoItem === 'prodotto' ? 'prodotto' : 'componente'}`)
      return
    }

    setItemsSelezionati([...itemsSelezionati, { 
      ...item, 
      quantita: 1,
      disponibile: disponibilita.disponibile,
      riservata: disponibilita.riservata,
      effettiva: disponibilita.effettiva,
    }])
    setSearchItem('')
    setShowDropdown(false)
  }

  const removeItem = (id) => {
    setItemsSelezionati(itemsSelezionati.filter(i => i.id !== id))
  }

  const updateQuantita = (id, quantita) => {
    const item = itemsSelezionati.find(i => i.id === id)
    const qty = parseInt(quantita) || 1

    const maxScaricabile = tipoItem === 'componente' ? item.effettiva : item.disponibile
    if (qty > maxScaricabile) {
      toast.error(`Quantità massima scaricabile: ${maxScaricabile}`)
      return
    }

    setItemsSelezionati(itemsSelezionati.map(i => 
      i.id === id ? { ...i, quantita: qty } : i
    ))
  }

  const changeTipo = (newTipo) => {
    setTipoItem(newTipo)
    setItemsSelezionati([])
    setSearchItem('')
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (itemsSelezionati.length === 0) {
      toast.error(`Aggiungi almeno un ${tipoItem === 'prodotto' ? 'prodotto' : 'componente'}`)
      return
    }

    if (!causale.trim()) {
      toast.error('Inserisci la causale dello scarico')
      return
    }
    if (!categoriaCausale) {
      toast.error('Seleziona il motivo dello scarico')
      return
    }
    if (/^\d+$/.test(causale.trim())) {
      toast.error('La causale non può essere solo numerica (es: "15"). Inserisci una descrizione.')
      return
    }

    // Validazione finale giacenze
    for (const item of itemsSelezionati) {
      const maxScaricabile = tipoItem === 'componente' ? item.effettiva : item.disponibile
      if (item.quantita > maxScaricabile) {
        toast.error(`${item.nome}: quantità richiesta (${item.quantita}) > max scaricabile (${maxScaricabile})`)
        return
      }
    }

    const movimenti = itemsSelezionati.map(item => {
      const movimento = {
        quantita: item.quantita,
        causale: causale.trim(),
        categoria_causale: categoriaCausale,
        note: note.trim(),
      }
      
      if (tipoItem === 'prodotto') {
        movimento.prodotto = item.id
      } else if (tipoItem === 'box') {
        movimento.box = item.id
      } else {
        movimento.componente = item.id
      }
      
      return movimento
    })

    scaricoMutation.mutate(movimenti)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Scarico Merce</h1>
          <p className="text-muted-foreground">
            Registra uno scarico di prodotti, componenti o box assemblate
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5" />
              Dati Scarico
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Tipo Item */}
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={tipoItem === 'prodotto' ? 'default' : 'outline'}
                  onClick={() => changeTipo('prodotto')}
                  className="flex-1"
                >
                  Prodotti
                </Button>
                <Button
                  type="button"
                  variant={tipoItem === 'componente' ? 'default' : 'outline'}
                  onClick={() => changeTipo('componente')}
                  className="flex-1"
                >
                  Componenti
                </Button>
                <Button
                  type="button"
                  variant={tipoItem === 'box' ? 'default' : 'outline'}
                  onClick={() => changeTipo('box')}
                  className="flex-1"
                >
                  Box assemblate
                </Button>
              </div>
            </div>

            {/* Autocomplete Items */}
            <div className="space-y-2">
              <Label htmlFor="search-item">
                Cerca e aggiungi {tipoItem === 'prodotto' ? 'prodotti' : (tipoItem === 'box' ? 'box assemblate' : 'componenti')} *
              </Label>
              <div className="relative" ref={dropdownRef}>
                <Input
                  id="search-item"
                  value={searchItem}
                  onChange={(e) => {
                    setSearchItem(e.target.value)
                    setShowDropdown(true)
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder={`Digita nome o codice ${tipoItem === 'prodotto' ? 'prodotto' : (tipoItem === 'box' ? 'box' : 'componente')}...`}
                />
                {showDropdown && searchItem && isSearching && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg p-4 text-center text-muted-foreground">
                    Ricerca in corso...
                  </div>
                )}
                {showDropdown && searchItem && !isSearching && itemsFiltrati.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                    {itemsFiltrati.slice(0, 10).map((item) => {
                      const disponibilita = getDisponibilitaItem(item.id)
                      const badgeValue = tipoItem === 'componente'
                        ? disponibilita.effettiva
                        : disponibilita.disponibile
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => addItem(item)}
                          className="w-full px-4 py-2 text-left hover:bg-accent flex items-center justify-between"
                        >
                          <div>
                            <div className="font-medium">{item.nome}</div>
                            <div className="text-sm text-muted-foreground">{item.codice}</div>
                          </div>
                          <Badge variant={badgeValue > 0 ? "outline" : "destructive"}>
                            {tipoItem === 'componente' ? `Eff: ${badgeValue}` : `Disp: ${badgeValue}`}
                          </Badge>
                        </button>
                      )
                    })}
                  </div>
                )}
                {showDropdown && searchItem && !isSearching && itemsFiltrati.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg p-4 text-center text-muted-foreground">
                    Nessun articolo trovato
                  </div>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                ⚠️ La quantità da scaricare non può superare la disponibilità valida
                {tipoItem === 'componente' ? ' (effettiva = disponibile - riservata)' : ''}
              </p>
            </div>

            {/* Tabella Items Selezionati */}
            {itemsSelezionati.length > 0 && (
              <div className="space-y-2">
                <Label>
                  Articoli da scaricare ({itemsSelezionati.length})
                </Label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Codice</TableHead>
                      <TableHead>Nome</TableHead>
                      {tipoItem === 'componente' && <TableHead>Tipo</TableHead>}
                      <TableHead className="text-center">Disponibile</TableHead>
                      {tipoItem === 'componente' && <TableHead className="text-center">Riservata</TableHead>}
                      {tipoItem === 'componente' && <TableHead className="text-center">Effettiva</TableHead>}
                      <TableHead className="w-32">Quantità</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itemsSelezionati.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-sm">{item.codice}</TableCell>
                        <TableCell>{item.nome}</TableCell>
                        {tipoItem === 'componente' && (
                          <TableCell>
                            <Badge variant="outline">{item.tipo_display}</Badge>
                          </TableCell>
                        )}
                        <TableCell className="text-center">
                          <Badge variant={item.disponibile > 0 ? "outline" : "destructive"}>
                            {item.disponibile}
                          </Badge>
                        </TableCell>
                        {tipoItem === 'componente' && (
                          <TableCell className="text-center">
                            <Badge variant="secondary">
                              {item.riservata}
                            </Badge>
                          </TableCell>
                        )}
                        {tipoItem === 'componente' && (
                          <TableCell className="text-center">
                            <Badge variant={item.effettiva > 0 ? "outline" : "destructive"}>
                              {item.effettiva}
                            </Badge>
                          </TableCell>
                        )}
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            max={tipoItem === 'componente' ? item.effettiva : item.disponibile}
                            value={item.quantita}
                            onChange={(e) => updateQuantita(item.id, e.target.value)}
                            className="w-24"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(item.id)}
                            className="text-red-600"
                          >
                            <XIcon className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="categoria-causale-scarico">Motivo dello scarico *</Label>
              <select
                id="categoria-causale-scarico"
                value={categoriaCausale}
                onChange={(e) => setCategoriaCausale(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
                required
              >
                <option value="">Seleziona motivo</option>
                <option value="scarto">Scarto / rottura</option>
                <option value="uso_interno">Uso interno</option>
                <option value="rettifica">Rettifica inventario</option>
                <option value="altro">Altro scarico</option>
              </select>
              <p className="text-xs text-muted-foreground">Le spedizioni degli ordini sono classificate automaticamente.</p>
            </div>

            {/* Causale */}
            <div className="space-y-2">
              <Label htmlFor="causale">Causale *</Label>
              <Input
                id="causale"
                value={causale}
                onChange={(e) => setCausale(e.target.value)}
                placeholder="Es: Vendita ordine #123, Scarto, Utilizzo interno"
              />
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label htmlFor="note">Note (opzionale)</Label>
              <Textarea
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note aggiuntive..."
                rows={3}
              />
            </div>

            {/* Azioni */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={scaricoMutation.isPending}
                variant="destructive"
              >
                {scaricoMutation.isPending ? 'Registrazione...' : 'Registra Scarico'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
